import React, { useState } from 'react';
import styled from 'styled-components';
import { FaCar, FaMapMarkerAlt, FaClock, FaStar } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';

const ActivityContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FilterSection = styled.section`
  background: var(--bg-card);
  border-radius: var(--border-radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--bg-secondary);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 2px;
  }
`;

const FilterButton = styled.button<{ isActive: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.isActive ? 'var(--primary-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  background: ${props => props.isActive ? 'var(--bg-hover)' : 'var(--bg-card)'};
  color: ${props => props.isActive ? 'var(--primary-color)' : 'var(--text-secondary)'};
  font-weight: ${props => props.isActive ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: var(--primary-color);
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActivityCard = styled.div`
  background: var(--bg-card);
  border-radius: var(--border-radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActivityType = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  
  svg {
    font-size: 1.25rem;
  }
`;

const ActivityDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  
  svg {
    font-size: 1rem;
  }
`;

const ActivityRoute = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RoutePoint = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  
  svg {
    font-size: 1rem;
    color: var(--text-secondary);
  }
`;

const ActivityFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
`;

const ActivityPrice = styled.div`
  font-weight: 600;
  color: var(--text-primary);
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--primary-color);
  
  svg {
    font-size: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
  
  svg {
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--text-secondary);
  }
`;

const Activity: React.FC = () => {
  const { t } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');
  
  const filters = [
    { id: 'all', label: t('app.allTrips') },
    { id: 'upcoming', label: t('app.upcomingTrips') },
    { id: 'past', label: t('app.pastTrips') },
    { id: 'cancelled', label: t('app.cancelledTrips') }
  ];
  
  // Données de test
  const activities = [
    {
      id: 1,
      type: 'standard',
      date: '2024-03-20 14:30',
      origin: 'Paris, France',
      destination: 'Orly Airport',
      price: '45.00 €',
      rating: 4.5
    },
    {
      id: 2,
      type: 'luxury',
      date: '2024-03-18 09:15',
      origin: 'Paris, France',
      destination: 'Charles de Gaulle Airport',
      price: '75.00 €',
      rating: 5
    }
  ];
  
  return (
    <ActivityContainer>
      <FilterSection>
        <FilterButtons>
          {filters.map((filter) => (
            <FilterButton
              key={filter.id}
              isActive={activeFilter === filter.id}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
            </FilterButton>
          ))}
        </FilterButtons>
      </FilterSection>
      
      {activities.length > 0 ? (
        <ActivityList>
          {activities.map((activity) => (
            <ActivityCard key={activity.id}>
              <ActivityHeader>
                <ActivityType>
                  <FaCar />
                  <span>{t(`app.${activity.type}`)}</span>
                </ActivityType>
                <ActivityDate>
                  <FaClock />
                  <span>{activity.date}</span>
                </ActivityDate>
              </ActivityHeader>
              
              <ActivityRoute>
                <RoutePoint>
                  <FaMapMarkerAlt />
                  <span>{activity.origin}</span>
                </RoutePoint>
                <RoutePoint>
                  <FaMapMarkerAlt />
                  <span>{activity.destination}</span>
                </RoutePoint>
              </ActivityRoute>
              
              <ActivityFooter>
                <ActivityPrice>{activity.price}</ActivityPrice>
                <Rating>
                  <FaStar />
                  <span>{activity.rating}</span>
                </Rating>
              </ActivityFooter>
            </ActivityCard>
          ))}
        </ActivityList>
      ) : (
        <EmptyState>
          <FaCar />
          <p>{t('app.noTrips')}</p>
        </EmptyState>
      )}
    </ActivityContainer>
  );
};

export default Activity; 